"use client";

import { useEffect, useId, useRef, useState } from "react";
import styles from "./titration-reading-view.module.css";

export function practiceReadingSample(
  liveReadingMl: number,
  random = Math.random()
) {
  const sample =
    Math.round((0.5 + Math.max(0, Math.min(1, random)) * 24) * 100) / 100;
  return Math.abs(sample - liveReadingMl) <= 0.02
    ? Math.round((sample <= 24 ? sample + 0.5 : sample - 0.5) * 100) / 100
    : sample;
}

export function readingScaleWindow(readingMl: number) {
  const startMl = Math.max(
    0,
    Math.min(24.2, Math.floor(readingMl * 10) / 10 - 0.3)
  );
  return {
    startMl,
    endMl: startMl + 0.8,
    meniscusY: Math.max(
      40,
      Math.min(320, 40 + ((readingMl - startMl) / 0.8) * 280)
    ),
  };
}

export default function TitrationReadingView({
  readingMl,
  practice = false,
  onClose,
}: {
  readingMl: number;
  practice?: boolean;
  onClose: () => void;
}) {
  const gradientId = useId().replace(/:/g, "");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [estimate, setEstimate] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [feedback, setFeedback] = useState("");
  const { startMl, meniscusY } = readingScaleWindow(readingMl);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement as HTMLElement | null;
    const keepEscapeInDialog = (event: KeyboardEvent) => {
      if (event.key === "Escape") event.stopPropagation();
    };
    dialog?.addEventListener("keydown", keepEscapeInDialog);
    dialog?.showModal();
    return () => {
      dialog?.removeEventListener("keydown", keepEscapeInDialog);
      dialog?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="reading-title"
      onCancel={onClose}
    >
      <div className={styles.header}>
        <h2 id="reading-title">
          {practice
            ? "Practise reading the meniscus"
            : "Read the lower meniscus"}
        </h2>
        <button
          className={styles.close}
          type="button"
          onClick={onClose}
          aria-label="Close reading view"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <p className={styles.instruction}>
        Read the bottom of the curve at eye level.
      </p>
      <div className={styles.content}>
        <figure className={styles.figure}>
          <svg
            viewBox="0 0 260 360"
            className={styles.scale}
            role="img"
            aria-label="Eye-level burette scale with a concave lower meniscus"
          >
            <defs>
              <linearGradient id={`${gradientId}-glass`}>
                <stop offset="0" stopColor="#83999b" stopOpacity=".55" />
                <stop offset=".12" stopColor="#ffffff" stopOpacity=".95" />
                <stop offset=".28" stopColor="#e9f0ef" stopOpacity=".18" />
                <stop offset=".7" stopColor="#ffffff" stopOpacity=".08" />
                <stop offset=".9" stopColor="#b6c7c8" stopOpacity=".55" />
                <stop offset="1" stopColor="#f7ffff" stopOpacity=".95" />
              </linearGradient>
              <linearGradient id={`${gradientId}-liquid`}>
                <stop offset="0" stopColor="#91a7a8" stopOpacity=".45" />
                <stop offset=".18" stopColor="#dce8e7" stopOpacity=".3" />
                <stop offset=".5" stopColor="#ffffff" stopOpacity=".1" />
                <stop offset=".85" stopColor="#dce8e7" stopOpacity=".25" />
                <stop offset="1" stopColor="#91a7a8" stopOpacity=".45" />
              </linearGradient>
              <clipPath id={`${gradientId}-tube`}>
                <rect x="64" y="20" width="102" height="320" rx="5" />
              </clipPath>
            </defs>
            <rect
              x="34"
              y="12"
              width="192"
              height="336"
              rx="12"
              fill="#fff"
              stroke="#dce3df"
            />
            <rect
              x="62"
              y="20"
              width="106"
              height="320"
              rx="8"
              fill={`url(#${gradientId}-glass)`}
              stroke="#607877"
              strokeWidth="1.5"
            />
            <g clipPath={`url(#${gradientId}-tube)`}>
              <path
                d={`M 64 ${meniscusY - 12} Q 115 ${meniscusY + 12} 166 ${
                  meniscusY - 12
                } L 166 340 L 64 340 Z`}
                fill={`url(#${gradientId}-liquid)`}
              />
              <path
                d={`M 64 ${meniscusY - 12} Q 115 ${meniscusY + 12} 166 ${
                  meniscusY - 12
                }`}
                fill="none"
                stroke="#182f2e"
                strokeWidth="2.2"
              />
              <path
                d={`M 67 ${meniscusY - 13.5} Q 115 ${meniscusY + 9.5} 163 ${
                  meniscusY - 13.5
                }`}
                fill="none"
                stroke="#fff"
                strokeOpacity=".85"
                strokeWidth="1"
              />
            </g>
            <path
              d="M 72 26 V 334 M 160 26 V 334"
              stroke="#fff"
              strokeOpacity=".7"
              strokeWidth="2"
            />
            {Array.from({ length: 9 }, (_, i) => {
              const value = Math.round((startMl + i / 10) * 10) / 10;
              const major = Math.round(value * 10) % 5 === 0;
              const y = 40 + i * 35;
              return (
                <g key={i}>
                  <line
                    x1="78"
                    x2={major ? 106 : 94}
                    y1={y}
                    y2={y}
                    stroke="#fff"
                    strokeWidth={major ? 4 : 3}
                  />
                  <line
                    x1="78"
                    x2={major ? 106 : 94}
                    y1={y}
                    y2={y}
                    stroke="#203a37"
                    strokeWidth={major ? 2 : 1.3}
                  />
                  {major ? (
                    <text
                      x="181"
                      y={y + 6}
                      fill="#203a37"
                      fontSize="20"
                      fontWeight="600"
                    >
                      {value.toFixed(1)}
                    </text>
                  ) : null}
                </g>
              );
            })}
            <text x="184" y="27" fill="#49605a" fontSize="14">
              mL
            </text>
          </svg>
          <figcaption>Each division is 0.1 mL. Estimate to 0.01 mL.</figcaption>
        </figure>
        <div className={styles.entry}>
          {practice ? (
            <p className={styles.practice}>
              Separate practice sample. This reading does not change your trial.
            </p>
          ) : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const value = Number(estimate);
              if (estimate.trim() === "" || !Number.isFinite(value)) {
                setFeedback("Enter a reading in mL first.");
                return;
              }
              setRevealed(true);
              setFeedback(
                Math.abs(value - readingMl) <= 0.02
                  ? "Your estimate is within 0.02 mL of this modelled reading."
                  : "Compare your estimate with the bottom of the curve, then try again."
              );
            }}
          >
            <label htmlFor="meniscus-estimate">Your reading (mL)</label>
            <div className={styles.actions}>
              <input
                id="meniscus-estimate"
                inputMode="decimal"
                value={estimate}
                onChange={(event) => setEstimate(event.target.value)}
                placeholder="e.g. 6.56"
              />
              <button type="submit">Check reading</button>
            </div>
          </form>
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setRevealed(!revealed)}
          >
            {revealed ? "Hide" : "Reveal"} modelled reading
          </button>
          {revealed ? (
            <p className={styles.answer}>
              Modelled reading: <strong>{readingMl.toFixed(2)} mL</strong>
            </p>
          ) : null}
          <p className={styles.feedback} role="status">
            {feedback}
          </p>
        </div>
      </div>
    </dialog>
  );
}
